import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import apiManager from '../../../lib/api';
import { T, useTranslate } from '@tolgee/react';

interface PendingUser {
    id?: string;
    _id?: string;
    name: string;
    email: string;
    phone?: string;
    role: string;
    nif?: string;
    address?: {
        street?: string;
        city?: string;
        postalCode?: string;
        country?: string;
    };
    createdAt: string;
}

const PendingUsersPage = () => {
    const { t } = useTranslate();
    const [pendingUsers, setPendingUsers] = useState<PendingUser[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [actionLoading, setActionLoading] = useState<string | null>(null);
    const [rejectModal, setRejectModal] = useState<{ userId: string; userName: string } | null>(null);
    const [rejectReason, setRejectReason] = useState('');
    const [deleteOnReject, setDeleteOnReject] = useState(false);

    useEffect(() => {
        fetchPendingUsers();
    }, []);

    const fetchPendingUsers = async () => {
        setIsLoading(true);
        setError(null);

        try {
            const response = await apiManager.users.getPendingUsers();

            if (response.success && response.data) {
                setPendingUsers(response.data as unknown as PendingUser[]);
            } else {
                setError('Error al cargar usuarios pendientes');
            }
        } catch (err) {
            console.error('Error al cargar usuarios pendientes:', err);
            setError('Error al cargar usuarios pendientes');
        } finally {
            setIsLoading(false);
        }
    };

    const handleApprove = async (userId: string) => {
        setActionLoading(userId);
        try {
            const response = await apiManager.users.approveUser(userId);

            if (response.success) {
                // Eliminar de la lista
                setPendingUsers(pendingUsers.filter(u => (u.id || u._id) !== userId));
            } else {
                setError('Error al aprobar usuario');
            }
        } catch (err) {
            console.error('Error al aprobar usuario:', err);
            setError('Error al aprobar usuario');
        } finally {
            setActionLoading(null);
        }
    };

    const handleReject = async () => {
        if (!rejectModal) return;

        setActionLoading(rejectModal.userId);
        try {
            const response = await apiManager.users.rejectUser(
                rejectModal.userId,
                rejectReason,
                deleteOnReject
            );

            if (response.success) {
                // Eliminar de la lista
                setPendingUsers(pendingUsers.filter(u => (u.id || u._id) !== rejectModal.userId));
                setRejectModal(null);
                setRejectReason('');
                setDeleteOnReject(false);
            } else {
                setError('Error al rechazar usuario');
            }
        } catch (err) {
            console.error('Error al rechazar usuario:', err);
            setError('Error al rechazar usuario');
        } finally {
            setActionLoading(null);
        }
    };

    const getUserId = (user: PendingUser) => user.id || user._id || '';

    return (
        <AdminLayout title={t('admin.pending_users.title', 'Usuarios Pendientes de Aprobación')}>
            {/* Encabezado */}
            <div className="mb-6 bg-white p-4 rounded-lg shadow">
                <div className="flex justify-between items-center">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-800">
                            <T keyName="admin.pending_users.subtitle">Solicitudes de Registro</T>
                        </h2>
                        <p className="text-sm text-gray-600">
                            <T keyName="admin.pending_users.description">
                                Los siguientes usuarios han solicitado una cuenta y están esperando aprobación.
                            </T>
                        </p>
                    </div>
                    <Link
                        to="/admin/users"
                        className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                    >
                        <T keyName="admin.pending_users.view_all_users">Ver Todos los Usuarios</T>
                    </Link>
                </div>
            </div>

            {/* Lista de usuarios pendientes */}
            <div className="bg-white shadow rounded-lg">
                {isLoading ? (
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900"></div>
                    </div>
                ) : error ? (
                    <div className="p-4 text-red-700 bg-red-100 rounded-md">
                        <p>{error}</p>
                        <button
                            onClick={fetchPendingUsers}
                            className="mt-2 text-sm underline hover:no-underline"
                        >
                            Reintentar
                        </button>
                    </div>
                ) : pendingUsers.length === 0 ? (
                    <div className="p-8 text-center">
                        <div className="text-6xl mb-4">✅</div>
                        <h3 className="text-lg font-medium text-gray-900">
                            <T keyName="admin.pending_users.no_pending">No hay usuarios pendientes</T>
                        </h3>
                        <p className="text-gray-500 mt-2">
                            <T keyName="admin.pending_users.all_approved">
                                Todos los usuarios han sido aprobados o rechazados.
                            </T>
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead>
                                <tr className="bg-gray-50">
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        <T keyName="admin.pending_users.user">Usuario</T>
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        <T keyName="admin.pending_users.contact">Contacto</T>
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        <T keyName="admin.pending_users.role">Rol Solicitado</T>
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        <T keyName="admin.pending_users.date">Fecha de Registro</T>
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        <T keyName="admin.pending_users.actions">Acciones</T>
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {pendingUsers.map((user) => {
                                    const userId = getUserId(user);
                                    return (
                                        <tr key={userId} className="hover:bg-gray-50">
                                            <td className="px-6 py-4">
                                                <div className="text-sm font-medium text-gray-900">{user.name}</div>
                                                <div className="text-sm text-gray-500">{user.email}</div>
                                                {user.nif && (
                                                    <div className="text-xs text-gray-400">NIF: {user.nif}</div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="text-sm text-gray-900">{user.phone || '-'}</div>
                                                {user.address && (
                                                    <div className="text-xs text-gray-500">
                                                        {user.address.city}, {user.address.country}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full
                          ${user.role === 'distributor' ? 'bg-blue-100 text-blue-800' :
                                                        user.role === 'shop' ? 'bg-green-100 text-green-800' :
                                                            user.role === 'commercial' ? 'bg-yellow-100 text-yellow-800' :
                                                                'bg-gray-100 text-gray-800'}`}
                                                >
                                                    {user.role?.toUpperCase() || 'CUSTOMER'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="text-sm text-gray-500">
                                                    {new Date(user.createdAt).toLocaleDateString('es-ES', {
                                                        year: 'numeric',
                                                        month: 'short',
                                                        day: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <div className="flex justify-end space-x-2">
                                                    <button
                                                        onClick={() => handleApprove(userId)}
                                                        disabled={actionLoading === userId}
                                                        className="inline-flex items-center px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 disabled:opacity-50"
                                                    >
                                                        {actionLoading === userId ? (
                                                            <span className="animate-spin mr-1">⏳</span>
                                                        ) : (
                                                            <span className="mr-1">✓</span>
                                                        )}
                                                        <T keyName="admin.pending_users.approve">Aprobar</T>
                                                    </button>
                                                    <button
                                                        onClick={() => setRejectModal({ userId, userName: user.name })}
                                                        disabled={actionLoading === userId}
                                                        className="inline-flex items-center px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50"
                                                    >
                                                        <span className="mr-1">✕</span>
                                                        <T keyName="admin.pending_users.reject">Rechazar</T>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal de rechazo */}
            {rejectModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">
                            <T keyName="admin.pending_users.reject_user">Rechazar Usuario</T>
                        </h3>
                        <p className="text-gray-600 mb-4">
                            ¿Estás seguro de que deseas rechazar la solicitud de <strong>{rejectModal.userName}</strong>?
                        </p>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                <T keyName="admin.pending_users.reject_reason">Motivo del rechazo (opcional)</T>
                            </label>
                            <textarea
                                value={rejectReason}
                                onChange={(e) => setRejectReason(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-red-500 focus:border-red-500"
                                rows={3}
                                placeholder="Introduce un motivo que se enviará al usuario..."
                            />
                        </div>

                        <div className="mb-4">
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={deleteOnReject}
                                    onChange={(e) => setDeleteOnReject(e.target.checked)}
                                    className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                                />
                                <span className="ml-2 text-sm text-gray-600">
                                    <T keyName="admin.pending_users.delete_user">Eliminar usuario de la base de datos</T>
                                </span>
                            </label>
                        </div>

                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => {
                                    setRejectModal(null);
                                    setRejectReason('');
                                    setDeleteOnReject(false);
                                }}
                                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                            >
                                <T keyName="admin.pending_users.cancel">Cancelar</T>
                            </button>
                            <button
                                onClick={handleReject}
                                disabled={actionLoading === rejectModal.userId}
                                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                            >
                                {actionLoading === rejectModal.userId ? 'Rechazando...' : 'Rechazar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
};

export default PendingUsersPage;
